import { css } from "@emotion/react";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { type ReactNode, useEffect } from "react";

import { BetaPageHeader, Spacer } from "@zapier/design-system";

import { ErrorBoundary } from "@zapier/reporting/src/components/global/ErrorBoundary";
import { HistoryTabNavigation } from "@zapier/reporting/src/components/global/HistoryTabNavigation";
import { HISTORY_PAGE_SURFACE_ID } from "@zapier/reporting/src/constants";
import { useAccountAndUser } from "@zapier/reporting/src/hooks";
import {
  FilterChangeType,
  HistoryFiltersTypes,
} from "@zapier/reporting/src/types";
import { getResponsiveStyles } from "@zapier/reporting/src/utils";
import { identifyUser } from "@zapier/reporting/src/utils/qualaroo";
import Filters from "./Filters";
import type { HistoryFilterControl } from "./Filters/filterControls";
import { DateRangeLabel } from "./getDateOptions";
import { NotificationSettingsButton } from "./NotificationSettingsButton";

const SparkSurfaceLoader = dynamic(
  () => import("@zapier/spark-ui").then((mod) => mod.SparkSurfaceLoader),
  { ssr: false }
);

interface HistoryLayoutProps {
  actions?: ReactNode;
  banner?: ReactNode;
  children: ReactNode;
  defaultDateKey?: DateRangeLabel;
  description?: ReactNode;
  filters: HistoryFiltersTypes;
  hiddenFilters?: HistoryFilterControl[];
  maxDateRangeDays?: number;
  onFilterChange: FilterChangeType;
  onFilterReset: () => void;
  onRefreshData: () => void;
  title?: ReactNode;
}

const headerContentStyle = css`
  display: grid;
  gap: var(--zds-space-20);
`;

const navStyle = getResponsiveStyles({
  tablet: css`
    align-items: center;
    display: flex;
    gap: 16px;
    justify-content: space-between;
  `,
  mobile: css`
    display: grid;
    gap: 20px;
  `,
});

export function HistoryLayout({
  actions,
  banner,
  children,
  defaultDateKey,
  description,
  filters,
  hiddenFilters,
  maxDateRangeDays,
  onFilterChange,
  onFilterReset,
  onRefreshData,
  title,
}: HistoryLayoutProps) {
  const t = useTranslations("history-page");
  const { userId } = useAccountAndUser();

  useEffect(() => {
    if (userId) {
      identifyUser(userId);
    }
  }, [userId]);

  return (
    <>
      <BetaPageHeader
        title={title ?? t("title")}
        actions={
          <>
            {actions}
            <NotificationSettingsButton />
          </>
        }
        additionalContent={
          <div css={headerContentStyle}>
            <div css={navStyle}>
              <HistoryTabNavigation />
            </div>
            {description}
          </div>
        }
      />

      {banner}

      {HISTORY_PAGE_SURFACE_ID && (
        <ErrorBoundary fallback={<></>}>
          <SparkSurfaceLoader
            baseUrl={process.env.NEXT_PUBLIC_ZAPIER_ORIGIN}
            isLoggedIn={!!userId}
            surfaceId={HISTORY_PAGE_SURFACE_ID}
          />
          <Spacer height={20} />
        </ErrorBoundary>
      )}

      <Filters
        defaultDateKey={defaultDateKey}
        filters={filters}
        hiddenFilters={hiddenFilters}
        maxDateRangeDays={maxDateRangeDays}
        onClearFilters={onFilterReset}
        onFilterChange={onFilterChange}
        onRefreshData={onRefreshData}
      />
      <Spacer height={40} />
      {children}
    </>
  );
}
